import React, { useContext, useEffect, useRef, useState } from 'react'
import { Editor } from '@tinymce/tinymce-react'
import styled from 'styled-components'
import debounce from 'lodash.debounce'
import {
  getWordCountFromText,
  handleNotionOrExternalLink,
  removeSpecialCharacters,
} from '../../../lib/utils'
import { AccountContext } from '../../../providers/accountProvider'
import Exclamation from '../../Images/Exclamation'
import CrossDark from '../../Images/CrossDark'
import writeIcon from '../../../pages/Shows/Novels/Edit/writing-hand-light-skin-tone.svg'
import RedCircleCrossIcon from '../../Images/RedCircleCrossIcon'
import { handleNewEventLog } from '../../../lib/utils/events'

const CautionNoteDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  background-color: rgba(221, 54, 35, 0.24);
  padding: 8px 60px 8px 12px;
  border-radius: 4px;
  display: relative;

  @media (max-width: 600px) {
    margin: 0 16px;
    padding: 8px 40px 8px 12px;
  }
`

const CautionNoteText = styled.p`
  color: #666666;
  line-height: 16px;
  font-size: 12px;
`
const WriteImg = styled.img`
  width: 16px;
  height: 16px;
  margin: 0px 4px;
`

const CrossIcon = styled(CrossDark)`
  float: right;
`

const ExternalLink = styled.a`
  font-family: Noto Sans;
  font-size: 12px;
  font-weight: 500;
  line-height: 17px;
  color: #038ae3;
  cursor: pointer;
  font-weight: bold;
  text-decoration: underline;
`

const CautionNoteWrapper = styled.div`
  background-color: #ffffff;
  position: fixed;
  bottom: 6%;
  max-width: 912px;

  @media (max-width: 600px) {
    bottom: 8%;
  }
`

const CautionWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const RedCircleWrapper = styled.div`
  position: absolute;
  right: 12px;
  top: 10px;
  min-height: 24px;
  min-width: 24px;
  cursor: pointer;

  @media (max-width: 600px) {
    right: 24px;
  }
`

function getEditorHeight(isFirstChapterPublishBonus, isMobile) {
  return isMobile ? `calc(100vh - 64px)`  : '600px'
}
// function getEditorHeight(isFirstChapterPublishBonus, isMobile) {
//   return isMobile
//     ? isFirstChapterPublishBonus
//       ? `calc(100vh - 208px)`
//       : `100vh`
//     : '600px'
// }
const EditorWrapper = styled.div`
  height: ${(props) =>
    getEditorHeight(props.isFirstChapterPublishBonus, props.isMobile)};
  .tox-tinymce {
    border: none;
  }

  .tox .tox-toolbar__primary {
    background: #f0f1f5;
    border-radius: 200px;
    padding: 4px;
  }

  .tox-toolbar__primary {
    justify-content: flex-start !important;
  }

  .tox .tox-sidebar-wrap {
    margin-top: 84px;
    border-top: 1px solid #f0f0f0;

    @media (max-width: 600px) {
      margin-top: 0;
    }
  }

  .tox:not(.tox-tinymce-inline) .tox-editor-header {
    box-shadow: none;
  }

  .tox-toolbar__group {
    display: flex;
    gap: 6px;
  }

  .tox-editor-header {
    margin: 0 16px;
  }

  .tox-tinymce:not(.tox-tinymce-inline)
    .tox-editor-header:not(:first-child)
    .tox-toolbar:first-child {
    border: 0;
    border-radius: 24px;
  }

  .tox-tinymce:not(.tox-tinymce-inline)
    .tox-editor-header:not(:first-child)
    .tox-toolbar-overlord:first-child
    .tox-toolbar__primary {
    border: 0;
  }

  .tox-toolbar.tox-toolbar--scrolling {
    background-color: #f0f1f5;
  }

  @media (max-width: 600px) {
    .tox:not(.tox-tinymce-inline) .tox-editor-header {
      position: fixed;
      bottom: 12px;
      width: -webkit-fill-available;
    }
  }
`

const MyEditor = ({
  content,
  updateWordCount,
  chapter_id,
  user,
  novelDetails,
  isFirstChapterPublishBonus,
  isUgcBook,
  setNavMenuOpen,
}) => {
  const editorRef = useRef(null)
  const [showCautionNote, setShowCautionNote] = useState(true)
  const { isUgcUser, isHindiUgcUser } = useContext(AccountContext)
  const isMobile = window.screen.width < 600
  const toolbarPosition = isMobile ? 'bottom' : 'top'

  const handleEditorChange = () => {
    const editor = window.tinymce.activeEditor
    // const wordCount = editor.plugins.wordcount.getCount()
    // updateWordCount(wordCount)
    if (!editor) return
    const plainText = editor.getContent({ format: 'text' })
    const count = getWordCountFromText(removeSpecialCharacters(plainText))
    updateWordCount(count)
  }

  useEffect(() => {
    if (isUgcUser && process.env.REACT_APP_DEPLOY_ENV === 'production') {
      let scriptEle = document.createElement('script')
      scriptEle.setAttribute(
        'src',
        'https://svc.webspellchecker.net/spellcheck31/wscbundle/wscbundle.js'
      )
      scriptEle.setAttribute('type', 'text/javascript')
      document.body.appendChild(scriptEle)
      window.WEBSPELLCHECKER_CONFIG = {
        autoSearch: true,
        autoDestroy: true,
        autocorrect: true,
        autocomplete: true,
        enforceAI: true,
        ignoreWordsWithNumbers: false,
        removeBranding: true,
        serviceId: 'GLU5rtjbYaeeXaB',
        serviceProtocol: 'https',
        servicePort: '443',
        serviceHost: 'svc.webspellchecker.net',
        servicePath: 'api',
        actionItems: [
          'ignoreAll',
          'addWord',
          'ignore',
          'toggle',
          'proofreadDialog',
        ],
      }
    }
  }, [])

  const CautionText = () => {
    if (isUgcBook) {
      return (
        <CautionNoteText>
          Don't forget to check out our <WriteImg src={writeIcon} />
          <ExternalLink
            onClick={() =>
              handleNotionOrExternalLink(
                isHindiUgcUser
                  ? 'https://pocketnovel.notion.site/Writing-Tips-ad5d075c94bf4d368f0992dfa1b11cff'
                  : 'https://pocketnovel.notion.site/Writing-Tips-468f0ab41d964e3e8553c8129c5aa723'
              )
            }
          >
            Writing Guide
          </ExternalLink>{' '}
          to help you create amazing Audio series ! Note: It is strictly
          prohibited to upload any erotic, vulgar, political.{' '}
          <strong>More info:</strong>{' '}
          <ExternalLink
            onClick={() =>
              handleNotionOrExternalLink(
                'https://writer.pocketnovel.com/content-and-community-guidelines'
              )
            }
          >
            content guidelines
          </ExternalLink>
        </CautionNoteText>
      )
    }
    return (
      <CautionWrapper>
        <CautionNoteText>
          Uploading content that is erotic, vulgar, or political is strictly
          prohibited. If such content is identified and verified, the entire
          book might get blocked.
        </CautionNoteText>
        <ExternalLink
          onClick={() =>
            handleNotionOrExternalLink(
              'https://writer.pocketnovel.com/content-and-community-guidelines'
            )
          }
        >
          Click to Know more
        </ExternalLink>
      </CautionWrapper>
    )
  }

  const delayedHandleEditorChange = debounce(handleEditorChange, 1000)

  return (
    <EditorWrapper
      isFirstChapterPublishBonus={isFirstChapterPublishBonus}
      isMobile={isMobile}
    >
      <Editor
        id="editor"
        initialValue={content}
        // onInit={(evt, editor) => {editorRef.current = editor;console.log(editor);}}
        onInit={(evt, editor) => {
          editorRef.current = editor
          if (isUgcUser && process.env.REACT_APP_DEPLOY_ENV === 'production') {
            if (window.WEBSPELLCHECKER) {
              window.WEBSPELLCHECKER.init({
                container: editor.iframeElement,
              })
            }
          }
          editor.focus()
          handleEditorChange()
        }}
        onEditorChange={delayedHandleEditorChange}
        selector="textarea"
        apiKey="zbbryxi5kv4tdgq6s2l2wso42dv4oj0uwrfh8ls403w4j09o"
        onClick={() => setNavMenuOpen(false)}
        init={{
          // inline: true,
          height: '100%',
          skin: 'naked',
          // toolbar: [
          //   'undo redo | styleselect | bold italic | link image',
          //   'alignleft aligncenter alignright'
          // ],
          // toolbar1: 'undo redo | styleselect | bold italic | link image',
          // toolbar2: 'alignleft aligncenter alignright',
          menubar: false,
          branding: false,
          resize: false,
          statusbar: false,
          placeholder: 'Write what should not be forgotten....',
          content_style: `
          .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
            color: #c0c0c0;
          }

          .tox:not(.tox-tinymce-inline) .tox-editor-header {
            position: fixed;
            bottom: 12px;
          }
        `,
          toolbar: 'undo redo bold italic underline alignment',
          toolbar_groups: {
            alignment: {
              icon: 'align-center',
              tooltip: 'alignment',
              items: 'alignleft aligncenter alignright alignjustify',
            },
          },
          mobile: {
            toolbar_mode: 'floating',
          },
          toolbar_location: toolbarPosition,
          plugins: [
            'advlist autolink lists',
            'charmap print preview anchor help',
            'searchreplace visualblocks code save',
            'insertdatetime media table paste wordcount',
          ],
        }}
        name="body"
        // onEditorChange={this.handleEditorChange}
      />
      {showCautionNote && (
        <CautionNoteWrapper>
          <CautionNoteDiv>
            <CautionText />
            <RedCircleWrapper>
              <RedCircleCrossIcon
                onClick={() => {
                  handleNewEventLog(
                    'chapter_editor',
                    'novels_caution_text_closed',
                    {
                      route: window.location.pathname + window.location.search,
                      module_name: 'chapter_editor',
                      entity_id: chapter_id || 'Null',
                      entity_type: 'chapter',
                    },
                    user,
                    novelDetails
                  )
                  setShowCautionNote(false)
                }}
              />
            </RedCircleWrapper>
          </CautionNoteDiv>
        </CautionNoteWrapper>
      )}
    </EditorWrapper>
  )
}

export default MyEditor

export const getContent = () => {
  if (!window.tinymce) return ''
  const editor = window.tinymce.activeEditor
  if (!editor) return ''
  return editor.getContent()
}
export const getPlainContent = () => {
  if (!window.tinymce) return ''
  const editor = window.tinymce.activeEditor
  if (!editor) return ''
  return editor.getContent({ format: 'text' })
}
export const isContentChanged = () => {
  if (!window.tinymce) return
  const editor = window.tinymce.activeEditor
  if (!editor) return
  return editor.isDirty()
}
export const resetIsContentChanged = (status = false) => {
  if (!window.tinymce) return
  const editor = window.tinymce.activeEditor
  if (!editor) return
  editor.setDirty(status)
}
export const isRedoEmpty = () => {
  if (!window.tinymce) return
  const editor = window.tinymce.activeEditor
  if (!editor) return
  if (!editor.undoManager) return
  return !editor.undoManager.hasRedo()
}
export const isUndoEmpty = () => {
  if (!window.tinymce) return
  const editor = window.tinymce.activeEditor
  if (!editor) return
  if (!editor.undoManager) return
  return !editor.undoManager.hasUndo()
}
export const handleUndo = () => {
  if (!window.tinymce) return
  const editor = window.tinymce.activeEditor
  if (!editor) return
  if (!editor.undoManager) return
  editor.undoManager.undo()
}
export const handleRedo = () => {
  if (!window.tinymce) return
  const editor = window.tinymce.activeEditor
  if (!editor) return
  if (!editor.undoManager) return
  editor.undoManager.redo()
}
